import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@xeito/ui'
import { useAuth } from '~/lib/auth-context'
import { trpc } from '~/utils/trpc'

interface TermsDeclinedScreenProps {
  locale: string
  onBack: () => void
}

const EXIT_BUTTON =
  'w-full py-3 border border-[#00f0ff]/40 text-white hover:border-[#00f0ff] hover:text-[#00f0ff] font-mono text-sm rounded-lg bg-transparent transition-colors'
const EXIT_LINK =
  'block w-full py-3 text-center border border-[#00f0ff]/40 text-white hover:border-[#00f0ff] hover:text-[#00f0ff] font-mono text-sm rounded-lg no-underline transition-colors'
const TEXT_BUTTON = 'w-full text-center text-xs font-mono text-[#a0a0b0] underline hover:text-white'

/**
 * B-new-9684 — what a signed-in user who has NOT accepted the current Terms
 * can still do: export (GDPR Art. 15/20), delete (Art. 17), cancel a
 * subscription and exercise the right of withdrawal.
 *
 * Rendered by TermsAcceptanceGate, which sits ABOVE RouterProvider, so this
 * component has no router context: no `<Link>`, no `useNavigate`. Plain
 * anchors and a full navigation after deletion are deliberate. The two links
 * go to pages the gate lets through (`isTermsExitRoute`).
 *
 * The export and deletion calls mirror dashboard.settings.lazy.tsx.
 */
export function TermsDeclinedScreen({ locale, onBack }: TermsDeclinedScreenProps) {
  const { t } = useTranslation()
  const { signOut } = useAuth()
  const headingRef = useRef<HTMLHeadingElement>(null)

  // A view change is not a navigation, so move focus to the new heading.
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  const exportDataMutation = trpc.user.exportData.useMutation({
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `xeito-data-export-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      toast.success(t('settings.dataExportedSuccessfully'))
    },
    onError: () => {
      toast.error(t('settings.failedToExportData'))
    },
  })

  const deleteAccountMutation = trpc.user.deleteAccount.useMutation({
    onSuccess: async () => {
      await signOut()
      window.location.assign(`/${locale}`)
    },
    onError: () => {
      toast.error(t('settings.failedToDeleteAccount'))
    },
  })

  // The legal page is published in castellano only for gl (see
  // routes/$locale/withdrawal-rights.tsx), so link there directly.
  const withdrawalLocale = locale === 'gl' ? 'es' : locale

  return (
    <main
      id="main-content"
      role="main"
      data-testid="terms-declined-screen"
      className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-md border-2 border-[#00f0ff]/30 bg-[#0f0f15]/95 p-8 rounded-lg">
        <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-black mb-4 outline-none">
          {t('auth.termsDeclined.heading', "You haven't accepted the terms")}
        </h1>
        <p className="text-sm font-mono text-[#a0a0b0] mb-6">
          {t(
            'auth.termsDeclined.body',
            "Xeito can't be used without accepting the current Terms of Service and reading the Privacy Policy. Whatever you decide, you can still do the following.",
          )}
        </p>

        <div className="space-y-3 mb-6">
          <button
            type="button"
            onClick={() => exportDataMutation.mutate()}
            disabled={exportDataMutation.isPending}
            className={`${EXIT_BUTTON} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {exportDataMutation.isPending ? '...' : t('settings.downloadDataTitle')}
          </button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button type="button" className={`${EXIT_BUTTON} text-[#ff6b9d] border-[#ff6b9d]/40`}>
                {t('settings.deleteAccount')}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
              <AlertDialogHeader>
                <AlertDialogTitle>{t('settings.deleteAccount')}</AlertDialogTitle>
                <AlertDialogDescription>{t('settings.deleteConfirmationMessage')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteAccountMutation.mutate({ locale })}
                  disabled={deleteAccountMutation.isPending}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
                >
                  {t('settings.deleteAccount')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <a href={`/${locale}/dashboard/subscription`} className={EXIT_LINK}>
            {t('auth.termsDeclined.cancelSubscription', 'Cancel my subscription')}
          </a>
          <a href={`/${withdrawalLocale}/withdrawal-rights`} className={EXIT_LINK}>
            {t('auth.termsDeclined.withdraw', 'Exercise my right of withdrawal')}
          </a>
        </div>

        <div className="space-y-3 mb-6">
          <button type="button" onClick={onBack} className={TEXT_BUTTON}>
            {t('auth.termsDeclined.back', 'Back to accept the terms')}
          </button>
          <button type="button" onClick={() => signOut()} className={TEXT_BUTTON}>
            {t('auth.signOutLink', 'Sign out')}
          </button>
        </div>

        <p className="text-xs font-mono text-[#a0a0b0]">
          {t('auth.termsDeclined.contactIntro', 'Questions about your data or these terms?')}{' '}
          <a href="mailto:privacy@xeito.ai" className="text-[#00f0ff] hover:text-[#39ff14] underline">
            privacy@xeito.ai
          </a>
          {' · '}
          <a href="mailto:legal@xeito.ai" className="text-[#00f0ff] hover:text-[#39ff14] underline">
            legal@xeito.ai
          </a>
        </p>
      </div>
    </main>
  )
}
